/**
 * React wrapper over the unsaved-work registry (see unsavedWork.ts).
 *
 * Kept out of unsavedWork.ts so that file can stay import-free and testable
 * under --experimental-strip-types.
 *
 * Usage - the boolean is the whole contract, so make it mean "a reload right
 * now would lose something a person typed or picked":
 *
 *   useUnsavedWork('thread-composer', text.trim().length > 0);
 *
 * The label is only for debugging (/hub-check lists live claims). Instances
 * are namespaced with useId internally, so two mounts of the same component
 * cannot clear each other's claim and callers never have to invent ids.
 */

import { useEffect, useId } from 'react';
import { clearUnsavedWork, registerUnsavedWork } from './unsavedWork';

export function useUnsavedWork(label: string, dirty: boolean): void {
  const instanceId = useId();
  const key = `${label}#${instanceId}`;

  useEffect(() => {
    if (!dirty) {
      // Cleared eagerly rather than only on unmount: a composer that sends
      // its message stays mounted with an empty box, and leaving it "dirty"
      // until navigation would block the auto-update for no reason.
      clearUnsavedWork(key);
      return;
    }
    registerUnsavedWork(key);
    return () => clearUnsavedWork(key);
  }, [key, dirty]);

  // Unmount safety net. The effect above only registers a cleanup on the
  // dirty branch, so a component unmounting while dirty is covered - but a
  // changing label would leave the old key behind, and this catches that too.
  useEffect(() => () => clearUnsavedWork(key), [key]);
}
